import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useField } from 'informed';

import { Institute } from '@nw/shared/config';

import {
  InstituteApiVersion,
  useGetInstitutesMutation
} from '../../../hooks/useGetInstitutesMutation';
import { SEARCH_DEBOUNCE_MS } from '../../../constants';
import { getInstituteDetailsFromInstituteId } from '../../../utils/InstituteUtils';

import { ComboBox } from '../../ComboBox';
import { CustomLoaderIcon } from '../../../icons/CustomLoaderIcon';

import { FieldContainer } from './styledComponents';

const isAbortError = (error: unknown) =>
  error instanceof Error &&
  (error.name === 'AbortError' || error.name === 'CanceledError');

export interface InformedInstitutesComboboxProps {
  label: string;
  field: string;
  error: string;
  touched: boolean;
  isDisabled?: boolean;
  validationSchemaObject: any;
  placeholder?: string;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  fieldContainerStyles?: string;
  inputFieldStyles?: string;
  labelStyles?: string;
  listBoxStyles?: string;
  containerClassName?: string;
  reRenderObjects?: any[];
  helperText?: string;
  onSelectionChange?: (
    instituteId: string | null,
    instituteName?: string
  ) => void;
  handleOnBlur?: () => void;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  isMarginBottomZero?: boolean;
  otherField?: string;
  instituteApiVersion: InstituteApiVersion;
}

const InformedInstitutesCombobox = (props: InformedInstitutesComboboxProps) => {
  const {
    label,
    field,
    error,
    touched,
    isDisabled = false,
    validationSchemaObject,
    placeholder = 'Search institute...',
    isHidden,
    fieldWrapperStyles,
    fieldContainerStyles,
    inputFieldStyles,
    labelStyles,
    listBoxStyles,
    containerClassName,
    reRenderObjects,
    helperText,
    onSelectionChange,
    handleOnBlur,
    validateOnBlur = true,
    validateOnChange = true,
    isMarginBottomZero,
    otherField,
    instituteApiVersion
  } = props;

  const [institutesList, setInstitutesList] = useState<Institute[]>([]);
  const [currentInstitute, setCurrentInstitute] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  // Track custom value being set to prevent useEffect from overriding it
  const customValueRef = useRef<string | null>(null);
  /** Last successful empty-filter (`''`) result — used when input is cleared or API returns empty for baseline refresh */
  const baselineInstitutesRef = useRef<Institute[]>([]);
  /** Monotonic generation so stale async responses do not overwrite newer input */
  const institutesSearchIDRef = useRef(0);
  const searchDebounceRef = useRef<NodeJS.Timeout>();

  const { getInstitutesApi, status } = useGetInstitutesMutation(
    instituteApiVersion
  );

  const isBaselineLoadingRef = useRef(false);
  const getInstitutesApiRef = useRef(getInstitutesApi);

  useEffect(() => {
    getInstitutesApiRef.current = getInstitutesApi;
  }, [getInstitutesApi]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const loadBaselineInstitutes = useCallback(async () => {
    if (isBaselineLoadingRef.current) return;

    isBaselineLoadingRef.current = true;
    const currentInstitutesSearchID = ++institutesSearchIDRef.current;
    setIsInitialLoading(true);
    setHasLoadFailed(false);

    try {
      const data = await getInstitutesApiRef.current({ filterText: '' });
      if (currentInstitutesSearchID !== institutesSearchIDRef.current) return;

      baselineInstitutesRef.current = [...data.institutes];
      setInstitutesList(data.institutes);
    } catch (error) {
      if (currentInstitutesSearchID !== institutesSearchIDRef.current) return;
      if (isAbortError(error)) return;

      setHasLoadFailed(true);
      console.error('Error fetching institutes:', error);
    } finally {
      isBaselineLoadingRef.current = false;
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBaselineInstitutes();
  }, [loadBaselineInstitutes]);

  const handleOpenChange = useCallback(
    (isDropdownOpen: boolean) => {
      if (!isDropdownOpen) return;
      if (baselineInstitutesRef.current.length > 0) return;

      loadBaselineInstitutes();
    },
    [loadBaselineInstitutes]
  );

  // Primary field - stores institute_name (either from list or custom)
  // @ts-ignore - informed is a runtime property
  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: validateOnChange,
    // @ts-ignore - validationSchema is a runtime property
    validationSchema: validationSchemaObject?.fields?.[field],
    error: error,
    touched: touched,
    isDisabled,
    label,
    ...reRenderObjects
  });

  const { value } = fieldState;
  // @ts-ignore - informed is a runtime property
  const { onChange, onBlur } = informed;
  const { setValue, setTouched } = fieldApi;

  // Field API for otherField (to store custom institute value when "Other" is selected)
  // Always call useField to follow React hooks rules, but only use it if otherField is provided
  // @ts-ignore - useField is a runtime property
  const otherFieldApi = useField({
    field: otherField && otherField.trim() ? otherField : ''
  });

  // Sync currentInstitute with form value
  // Skip syncing if value is "Other" - we'll handle that separately to avoid race conditions
  useEffect(() => {
    if (value && typeof value === 'string' && value !== 'Other') {
      setCurrentInstitute(value);
      customValueRef.current = null; // Clear custom value ref when regular value is set
    } else if (!value) {
      setCurrentInstitute('');
      customValueRef.current = null;
    }
  }, [value]);

  // Separate effect to sync currentInstitute when otherField value changes (for "Other" case)
  // Use customValueRef if set, otherwise use otherField value
  useEffect(() => {
    if (value === 'Other') {
      if (customValueRef.current) {
        setCurrentInstitute(customValueRef.current);
      } else if (otherField && otherField.trim() && otherFieldApi?.fieldState?.value) {
        setCurrentInstitute(otherFieldApi.fieldState.value as string);
      }
    }
  }, [value, otherField, otherFieldApi?.fieldState?.value]);

  // Handle selection change
  const handleChange = useCallback(
    (selectedValue: string) => {
      const institute = getInstituteDetailsFromInstituteId(
        selectedValue,
        institutesList
      );
      // Handle "Other" selection - this means user selected the custom value from dropdown
      if ((selectedValue === 'Other' || customValueRef.current) && institute === undefined) {
        const customValue = customValueRef.current || (otherFieldApi?.fieldState?.value as string);
        if (customValue && otherField && otherField.trim()) {
          // Store custom value in ref
          customValueRef.current = customValue;
          // Set otherField with the custom value
          if (otherFieldApi?.fieldApi) {
            otherFieldApi.fieldApi.setValue(customValue);
          }
          // Keep currentInstitute showing the custom text
          setCurrentInstitute(customValue);
          // Set form value to "Other" for form submission
          onChange('Other');
          setTouched(true);
          onSelectionChange && onSelectionChange(null, customValue);
          return;
        }
      }

      if (institute) {
        // Set the institute_name as the field value (not institute_id)
        onChange(institute.institute_name);
        setValue(institute.institute_name);
        setCurrentInstitute(institute.institute_name);
        setTouched(true);
        // Clear otherField when a regular institute is selected (use undefined to omit from payload)
        if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
          otherFieldApi.fieldApi.setValue(undefined as any);
        }
        // Clear custom value ref when regular institute is selected
        customValueRef.current = null;
        onSelectionChange &&
          onSelectionChange(institute.institute_id, institute.institute_name);
      } else if (selectedValue && selectedValue.length > 0) {
        onChange(selectedValue);
        setValue(selectedValue);
        setCurrentInstitute(selectedValue);
        setTouched(true);
        // Clear otherField when a regular institute is selected (use undefined to omit from payload)
        if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
          otherFieldApi.fieldApi.setValue(undefined as any);
        }
        // Clear custom value ref when regular value is selected
        customValueRef.current = null;
        onSelectionChange && onSelectionChange(null, selectedValue);
      } else {
        // Clear selection
        onChange('');
        setValue('');
        setCurrentInstitute('');
        setTouched(true);
        // Clear otherField when selection is cleared (use undefined to omit from payload)
        if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
          otherFieldApi.fieldApi.setValue(undefined as any);
        }
        // Clear custom value ref when selection is cleared
        customValueRef.current = null;
        onSelectionChange && onSelectionChange(null);
      }
    },
    [institutesList, onChange, setValue, setTouched, onSelectionChange, otherFieldApi, otherField]
  );

  // Handle blur
  const handleBlur = useCallback(() => {
    // @ts-ignore - onBlur from Informed can be called without arguments
    onBlur();
    handleOnBlur && handleOnBlur();
  }, [onBlur, handleOnBlur]);

  // NOTE: We only track the input value here - we do NOT set field value
  // Field value is only set when user selects from dropdown or clicks "Add new" button
  // When user starts typing, reset the form value to clear previous selection
  const handleInputChange = useCallback(
    (val: string) => {
      setCurrentInstitute(val);

      if (val !== value) {
        onChange('');
        setValue('');
      }

      const trimmedQuery = val.trim();

      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = undefined;
      }

      if (trimmedQuery === '') {
        if (baselineInstitutesRef.current.length > 0) {
          institutesSearchIDRef.current += 1;
          setIsSearchPending(false);
          setHasLoadFailed(false);
          setInstitutesList([...baselineInstitutesRef.current]);
        } else {
          loadBaselineInstitutes();
        }
        return;
      }

      searchDebounceRef.current = setTimeout(async () => {
        const currentInstitutesSearchID = ++institutesSearchIDRef.current;
        setIsSearchPending(true);

        try {
          const response = await getInstitutesApiRef.current({
            filterText: trimmedQuery
          });
          if (currentInstitutesSearchID !== institutesSearchIDRef.current) {
            return;
          }
          setHasLoadFailed(false);
          setInstitutesList(response.institutes);
        } catch (error) {
          if (currentInstitutesSearchID !== institutesSearchIDRef.current) {
            return;
          }
          if (isAbortError(error)) {
            return;
          }
          console.error('Error fetching institutes:', error);
          setHasLoadFailed(true);
          setInstitutesList([]);
        } finally {
          if (currentInstitutesSearchID === institutesSearchIDRef.current) {
            setIsSearchPending(false);
          }
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    [value, onChange, setValue, loadBaselineInstitutes]
  );

  // Handle "Add new" button click - sets custom value when user clicks the button
  const handleButtonClick = useCallback(() => {
    const trimmedValue = currentInstitute?.trim();
    if (trimmedValue) {
      // Store custom value in ref to prevent useEffect from overriding it
      customValueRef.current = trimmedValue;
      // Set otherField FIRST before setting form value to "Other"
      // This ensures otherFieldApi.fieldState.value is available when useEffect runs
      if (otherField && otherField.trim() && otherFieldApi?.fieldApi) {
        otherFieldApi.fieldApi.setValue(trimmedValue);
      }
      // Keep currentInstitute showing the custom text, not "Other"
      setCurrentInstitute(trimmedValue);
      // Set form value to "Other" for form submission (don't use setValue here)
      onChange('Other');
      setTouched(true);
      onSelectionChange && onSelectionChange(null, trimmedValue);
    }
  }, [currentInstitute, onChange, setTouched, onSelectionChange, otherFieldApi]);

  const loadingState = useMemo(() => {
    if (isInitialLoading) {
      return 'loading';
    }
    if (isSearchPending) {
      return 'filtering';
    }
    if (hasLoadFailed) {
      return 'error';
    }
    return undefined;
  }, [isInitialLoading, isSearchPending, hasLoadFailed]);

  const extractMaxHeight = (styles?: string): number | undefined => {
    if (!styles) return undefined;
    const maxHeightMatch = styles.match(/max-h-(\d+)px/);
    if (maxHeightMatch) {
      return parseInt(maxHeightMatch[1], 10);
    }
    return undefined;
  };

  const maxHeight = extractMaxHeight(listBoxStyles) || 240;

  const combinedClassName = cn(fieldContainerStyles, containerClassName);

  // Memoize options to prevent unnecessary re-renders
  // Include custom value if it's set and not in the institutes list
  const options = useMemo(() => {
    const baseOptions = institutesList.map((institute) => ({
      label: institute.institute_name,
      value: institute.institute_id
    }));

    // If value is "Other", get the actual custom value from otherField
    let actualValue: string | null = typeof value === 'string' ? value : null;
    if (value === 'Other') {
      actualValue = customValueRef.current || (otherFieldApi?.fieldState?.value as string) || null;
    }

    // If there's a selected value that's not in the list (custom value), add it
    if (actualValue && typeof actualValue === 'string') {
      const isValueInList = institutesList.some(
        (inst) => inst.institute_name === actualValue
      );
      if (!isValueInList) {
        // Add custom value to the options so ComboBox can display it with the actual custom text
        return [{ label: actualValue, value: "Other" }, ...baseOptions];
      }
    }

    return baseOptions;
  }, [institutesList, value, otherFieldApi?.fieldState?.value]);

  const customLoader = useMemo(() => {
    return <CustomLoaderIcon />;
  }, []);

  const renderInstitutesCombobox = () => {
    return (
      <ComboBox
        name={field}
        label={label}
        value={currentInstitute || ''}
        placeholder={placeholder}
        options={options}
        onChange={handleChange}
        onBlur={handleBlur}
        onInputChange={handleInputChange}
        onOpenChange={handleOpenChange}
        error={error}
        helperText={helperText}
        isDisabled={isDisabled}
        isRequired={false}
        className={combinedClassName}
        labelClassName={labelStyles}
        inputClassName={inputFieldStyles}
        optionClassName={listBoxStyles}
        maxHeight={maxHeight}
        placement="bottom"
        shouldCloseOnBlur={true}
        closeOnScroll={true}
        enableAnimation={true}
        enableClientFilter={false}
        loadingState={loadingState}
        loadingMessage="Identifying relevant colleges..."
        customLoader={customLoader}
        getOptionValue={(opt) => opt.label}
        getOptionLabel={(opt) => opt.label}
        showButton={!!currentInstitute && currentInstitute?.length > 0}
        buttonLabel={`Create "${currentInstitute || ''}"`}
        onButtonClick={handleButtonClick}
        buttonVariant="ghost"
        buttonSize="sm"
      />
    );
  };

  return (
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderInstitutesCombobox()}
    </FieldContainer>
  );
};

InformedInstitutesCombobox.displayName = 'InformedInstitutesCombobox';

export { InformedInstitutesCombobox };
